import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import { useMeCore } from "../me";
import { useInstagramLoginCore } from "./index";

const OAUTH_MESSAGE_FROM = "Instagram Login";
const POPUP_NAME = "instagram_login_oauth";

export function useInstagramLoginOAuth(onSuccess?: () => void) {
  const intl = useIntl();
  const { company, getMeSocialSettings } = useMeCore();
  const { login, bind } = useInstagramLoginCore();
  const popupRef = useRef<Window | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.from !== OAUTH_MESSAGE_FROM) return;

      popupRef.current?.close();
      popupRef.current = null;

      if (event.data?.error) {
        toast.error(
          event.data?.errorMessage ||
            intl.formatMessage({ id: "app.instagramLogin.toast.error" })
        );
        return;
      }

      const code = event.data?.code as string | undefined;
      const state = event.data?.state as string | undefined;
      if (!code || !company?.id) return;

      void bind(company.id, code, () => {
        getMeSocialSettings(company.id);
        onSuccessRef.current?.();
      }, state);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [bind, company, getMeSocialSettings, intl]);

  const startLogin = useCallback(async () => {
    if (!company?.id) return;

    const url = await login(company.id);
    if (!url) return;

    const width = 520;
    const height = 720;
    const left = Math.max(
      0,
      window.screenX + (window.outerWidth - width) / 2
    );
    const top = Math.max(
      0,
      window.screenY + (window.outerHeight - height) / 2
    );

    popupRef.current = window.open(
      url,
      POPUP_NAME,
      `popup=yes,width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    if (!popupRef.current) {
      toast.error(
        intl.formatMessage({ id: "app.instagramLogin.toast.popupBlocked" })
      );
    }
  }, [company, login, intl]);

  return { startLogin };
}
